import { useTranslation, t } from '../../../i18n';
import React, { useState, useEffect, useRef } from 'react';
import {
    Card,
    Table,
    Button,
    Space,
    Modal,
    Form,
    Input,
    message,
    Popconfirm,
    Breadcrumb,
    Upload,
    Dropdown,
    Select,
    Tag,
    Switch,
    List,
    Typography,
    Alert,
    Drawer,
    Radio
} from 'antd';
import {
    FolderOutlined,
    FileOutlined,
    PlusOutlined,
    UploadOutlined,
    DownloadOutlined,
    EditOutlined,
    DeleteOutlined,
    HomeOutlined,
    CopyOutlined,
    ScissorOutlined,
    LockOutlined,
    CompressOutlined,
    FileZipOutlined,
    MoreOutlined,
    ReloadOutlined,
    CloudDownloadOutlined
} from '@ant-design/icons';
import Editor from '@monaco-editor/react';
import hostSharedService from '../../../../shared/services/hostSharedService';

const { TextArea } = Input;
const { Text, Paragraph } = Typography;

// 根据文件扩展名获取 Monaco Editor 的语言
const getLanguageFromFilename = (filename) => {
    if (!filename) return 'plaintext';
    const ext = filename.split('.').pop().toLowerCase();
    const languageMap = {
        'js': 'javascript',
        'jsx': 'javascript',
        'ts': 'typescript',
        'tsx': 'typescript',
        'json': 'json',
        'html': 'html',
        'htm': 'html',
        'css': 'css',
        'scss': 'scss',
        'less': 'less',
        'php': 'php',
        'py': 'python',
        'java': 'java',
        'c': 'c',
        'cpp': 'cpp',
        'cs': 'csharp',
        'go': 'go',
        'rs': 'rust',
        'rb': 'ruby',
        'sh': 'shell',
        'bash': 'shell',
        'sql': 'sql',
        'xml': 'xml',
        'yaml': 'yaml',
        'yml': 'yaml',
        'md': 'markdown',
        'txt': 'plaintext',
        'log': 'plaintext'
    };
    return languageMap[ext] || 'plaintext';
};

const guessFilenameFromUrl = (url) => {
    if (!url) {
        return '';
    }
    try {
        const parsed = new URL(url);
        const pathname = parsed.pathname || '';
        const segment = pathname.split('/').filter(Boolean).pop();
        return segment ? decodeURIComponent(segment) : '';
    } catch (error) {
        const sanitized = url.split('?')[0];
        const segment = sanitized.split('/').filter(Boolean).pop();
        return segment ? decodeURIComponent(segment) : '';
    }
};

const normalizeDirInput = (dir) => {
    if (!dir) {
        return '/';
    }
    let result = dir.trim();
    if (!result.startsWith('/')) {
        result = `/${result}`;
    }
    result = result.replace(/\/{2,}/g, '/');
    return result === '' ? '/' : result;
};

const buildTargetPath = (dir, filename) => {
    const safeDir = normalizeDirInput(dir);
    const trimmedFile = (filename || '').trim().replace(/^\/+/, '');
    if (!trimmedFile) {
        return safeDir;
    }
    const base = safeDir.endsWith('/') && safeDir !== '/' ? safeDir.slice(0, -1) : safeDir;
    return `${base === '' ? '/' : base}/${trimmedFile}`.replace(/\/{2,}/g, '/');
};

const sanitizeOutput = (text) => {
    if (!text) return '';
    if (typeof text !== 'string') {
        return JSON.stringify(text);
    }
    return text.replace(/\r/g, '').replace(/\n{3,}/g, '\n\n');
};

const TRUNCATE_OUTPUT = 1200;

const ensureOutputLength = (text) => {
    if (!text) return '';
    return text.length > TRUNCATE_OUTPUT ? `${text.slice(0, TRUNCATE_OUTPUT)}...` : text;
};

const deriveTaskStatus = (code, output, responseData) => {
    const normalizedCode = typeof code === 'number' ? code : parseInt(code || '0', 10);
    if (!Number.isNaN(normalizedCode) && normalizedCode >= 400) {
        return 'error';
    }
    const text = (output || '').toLowerCase();
    const explicitSuccess =
        (responseData && responseData.success === true) ||
        (responseData && responseData.code && responseData.code >= 200 && responseData.code < 300) ||
        (responseData?.data && responseData.data.code && responseData.data.code >= 200 && responseData.data.code < 300);
    if (explicitSuccess) {
        return 'success';
    }
    if (/error|fail|denied|invalid|not\s+found|失败|错误/.test(text)) {
        return 'error';
    }
    if (/success|complete|completed|done|100%|下载完成|已保存/.test(text)) {
        return 'success';
    }
    return 'running';
};

const REMOTE_STATUS_META = {
    running: { color: 'blue', label: 'Downloading' },
    success: { color: 'green', label: 'Completed' },
    error: { color: 'red', label: 'Failed' },
    timeout: { color: 'orange', label: 'Timeout' }
};

const MAX_POLL_ATTEMPTS = 60;
const POLL_INTERVAL = 4000;

const formatAccessLabel = (action) => {
    if (!action) {
        return t('None');
    }
    if (action === 'deny') {
        return t('Lock');
    }
    if (action === 'static') {
        return t('Static');
    }
    if (action.startsWith('auth:')) {
        const detail = action.slice(5);
        return detail ? t('HTTP authentication ({{detail}})', { detail: detail }) : t('HTTP authentication');
    }
    if (action.startsWith('ip:')) {
        return t('Allow IP {{value}}', { value: action.slice(3) });
    }
    return action;
};

const getAccessTagColor = (action) => {
    if (!action) {
        return 'default';
    }
    if (action === 'deny') {
        return 'red';
    }
    if (action === 'static') {
        return 'purple';
    }
    if (action.startsWith('auth:')) {
        return 'blue';
    }
    if (action.startsWith('ip:')) {
        return 'orange';
    }
    return 'default';
};

const buildAccessFormValues = (action) => {
    if (!action) {
        return { act: 'clear', auth_user: '', ip: '' };
    }
    if (action === 'deny' || action === 'static') {
        return { act: action, auth_user: '', ip: '' };
    }
    if (action.startsWith('auth:')) {
        return { act: 'auth', auth_user: action.slice(5), ip: '' };
    }
    if (action.startsWith('ip:')) {
        return { act: 'ip', ip: action.slice(3), auth_user: '' };
    }
    return { act: 'clear', auth_user: '', ip: '' };
};

const FileManager = ({ vhostId, vhostName }) => {
    const { t } = useTranslation();
    const [files, setFiles] = useState([]);
    const [currentPath, setCurrentPath] = useState('/');
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(0);
    const [createFolderVisible, setCreateFolderVisible] = useState(false);
    const [createFileVisible, setCreateFileVisible] = useState(false);
    const [editFileVisible, setEditFileVisible] = useState(false);
    const [editingFile, setEditingFile] = useState(null);
    const [fileContent, setFileContent] = useState('');
    const [renameVisible, setRenameVisible] = useState(false);
    const [renamingFile, setRenamingFile] = useState(null);
    const [chmodVisible, setChmodVisible] = useState(false);
    const [chmodFile, setChmodFile] = useState(null);
    const [decompressVisible, setDecompressVisible] = useState(false);
    const [decompressFile, setDecompressFile] = useState(null);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [folderForm] = Form.useForm();
    const [fileForm] = Form.useForm();
    const [renameForm] = Form.useForm();
    const [chmodForm] = Form.useForm();
    const [decompressForm] = Form.useForm();
    const [remoteDownloadForm] = Form.useForm();
    const [remoteDownloadVisible, setRemoteDownloadVisible] = useState(false);
    const [remoteDownloadLoading, setRemoteDownloadLoading] = useState(false);
    const [remoteSessions, setRemoteSessions] = useState([]);
    const [remoteTaskDrawerVisible, setRemoteTaskDrawerVisible] = useState(false);
    const pollingTimers = useRef({});
    const [accessForm] = Form.useForm();
    const [fileAccessMap, setFileAccessMap] = useState({});
    const [accessModalVisible, setAccessModalVisible] = useState(false);
    const [accessTarget, setAccessTarget] = useState(null);
    const [accessSubmitting, setAccessSubmitting] = useState(false);

    useEffect(() => {
        if (!vhostId) {
            return;
        }
        loadFiles(currentPath);
        // 目录变化时重置选中
        setSelectedFiles([]);
    }, [currentPath, vhostId]);

    useEffect(() => () => {
        Object.values(pollingTimers.current).forEach((timer) => {
            clearTimeout(timer);
        });
        pollingTimers.current = {};
    }, []);

    useEffect(() => {
        if (vhostName) {
            loadFileAccess();
        } else {
            setFileAccessMap({});
        }
    }, [vhostName]);

    const loadFiles = async (path) => {
        if (!vhostId) {
            return;
        }
        setLoading(true);
        try {
            const response = await hostSharedService.getFiles(vhostId, path);
            if (response.success) {
                setFiles(response.data || []);
            } else {
                message.error(response.error || t('Failed to load file list'));
            }
        } catch (error) {
            message.error(t('Failed to load file list'));
        } finally {
            setLoading(false);
        }
    };

    const loadFileAccess = async () => {
        if (!vhostName) {
            setFileAccessMap({});
            return;
        }

        try {
            const response = await hostSharedService.getFileAccess(vhostName);
            if (response.success) {
                setFileAccessMap(response.data || {});
            } else {
                message.error(response.error || t('Failed to load access control'));
                setFileAccessMap({});
            }
        } catch (error) {
            message.error(t('Failed to load access control: {{message}}', { message: error.message }));
            setFileAccessMap({});
        } finally {
        }
    };

    const openAccessModal = (record) => {
        const action = fileAccessMap[record.path] || '';
        accessForm.setFieldsValue(buildAccessFormValues(action));
        setAccessTarget({
            path: record.path,
            is_dir: record.type === 'directory' ? 1 : 0
        });
        setAccessModalVisible(true);
    };

    const closeAccessModal = () => {
        setAccessModalVisible(false);
        accessForm.resetFields();
        setAccessTarget(null);
    };

    const handleSubmitAccess = async () => {
        try {
            const values = await accessForm.validateFields();
            if (!accessTarget || !accessTarget.path) {
                message.error(t('Select a file or directory'));
                return;
            }
            setAccessSubmitting(true);

            const payload = {
                file: accessTarget.path,
                is_dir: accessTarget.is_dir,
                act: values.act
            };

            if (values.act === 'auth') {
                payload.auth_user = values.auth_user || '';
            }

            if (values.act === 'ip') {
                payload.ip = values.ip || '';
            }

            const response = await hostSharedService.setFileAccess(vhostName, payload);
            if (response.success) {
                message.success(t('File access control updated'));
                closeAccessModal();
                loadFileAccess();
            } else {
                message.error(response.error || t('Update failed'));
            }
        } catch (error) {
            if (!error.errorFields) {
                message.error(t('Update failed: {{message}}', { message: error.message }));
            }
        } finally {
            setAccessSubmitting(false);
        }
    };

    const handleNavigate = (path) => {
        setCurrentPath(path);
    };

    const handleCreateFolder = async (values) => {
        try {
            const folderPath = `${currentPath === '/' ? '' : currentPath}/${values.name}`;
            const response = await hostSharedService.createFolder(vhostId, folderPath);
            if (response.success) {
                message.success(t('Folder created'));
                setCreateFolderVisible(false);
                folderForm.resetFields();
                loadFiles(currentPath);
            } else {
                message.error(response.error || t('Creation failed'));
            }
        } catch (error) {
            message.error(t('Creation failed'));
        }
    };

    const handleCreateFile = async (values) => {
        try {
            const filePath = `${currentPath === '/' ? '' : currentPath}/${values.name}`;
            const response = await hostSharedService.createFile(vhostId, filePath, values.content || '');
            if (response.success) {
                message.success(t('File created'));
                setCreateFileVisible(false);
                fileForm.resetFields();
                loadFiles(currentPath);
            } else {
                message.error(response.error || t('Creation failed'));
            }
        } catch (error) {
            message.error(t('Creation failed'));
        }
    };

    const handleEditFile = async (file) => {
        try {
            // 检查文件大小（20MB = 20 * 1024 * 1024）
            const maxSize = 20 * 1024 * 1024;
            if (file.size > maxSize) {
                message.error(t('Files larger than 20MB cannot be edited online'));
                return;
            }

            const response = await hostSharedService.readFile(vhostId, file.path);
            if (response.success) {
                setEditingFile(file);
                setFileContent(response.data.content || '');
                setEditFileVisible(true);
            } else {
                message.error(response.error || t('Failed to read the file'));
            }
        } catch (error) {
            message.error(t('Failed to read the file'));
        }
    };

    const handleSaveFile = async () => {
        try {
            const response = await hostSharedService.writeFile(vhostId, editingFile.path, fileContent);
            if (response.success) {
                message.success(t('File saved'));
                setEditFileVisible(false);
                loadFiles(currentPath);
            } else {
                message.error(response.error || t('Failed to save'));
            }
        } catch (error) {
            message.error(t('Failed to save'));
        }
    };

    const handleDeleteFile = async (filePath) => {
        try {
            const response = await hostSharedService.deleteFile(vhostId, filePath);
            if (response.success) {
                message.success(t('Deleted successfully'));
                loadFiles(currentPath);
            } else {
                message.error(response.error || t('Deletion failed'));
            }
        } catch (error) {
            message.error(t('Deletion failed'));
        }
    };

    const handleRename = async (values) => {
        try {
            const response = await hostSharedService.renameFile(
                vhostId,
                renamingFile.path,
                `${currentPath === '/' ? '' : currentPath}/${values.newname}`
            );
            if (response.success) {
                message.success(t('Renamed successfully'));
                setRenameVisible(false);
                renameForm.resetFields();
                loadFiles(currentPath);
            } else {
                message.error(response.error || t('Rename failed'));
            }
        } catch (error) {
            message.error(t('Rename failed'));
        }
    };

    const handleChmod = async (values) => {
        try {
            const response = await hostSharedService.chmodFile(vhostId, chmodFile.path, values.mode);
            if (response.success) {
                message.success(t('Permissions changed'));
                setChmodVisible(false);
                chmodForm.resetFields();
                loadFiles(currentPath);
            } else {
                message.error(response.error || t('Failed to change permissions'));
            }
        } catch (error) {
            message.error(t('Failed to change permissions'));
        }
    };

    const handleUpload = async ({ file, onSuccess, onError }) => {
        setUploading((n) => n + 1);
        try {
            const response = await hostSharedService.uploadFile(vhostId, currentPath, file);
            if (response.success) {
                message.success(t('Uploaded: {{name}}', { name: file.name }));
                onSuccess && onSuccess(response.data);
                loadFiles(currentPath);
            } else {
                message.error(response.error || t('Upload failed'));
                onError && onError(new Error(response.error || 'upload failed'));
            }
        } catch (error) {
            message.error(t('Upload failed'));
            onError && onError(error);
        } finally {
            setUploading((n) => n - 1);
        }
    };

    const handleDownload = async (file) => {
        try {
            // 构建下载URL - 直接访问CDN API，token放在URL参数里
            const jwtData = await hostSharedService.getFileJWT(vhostId);
            if (!jwtData.success) {
                message.error(t('Failed to retrieve download token'));
                return;
            }

            const baseUrl = jwtData.data.api_url.includes('index.php')
                ? jwtData.data.api_url
                : (jwtData.data.api_url.endsWith('/') ? jwtData.data.api_url + 'api/index.php' : jwtData.data.api_url + '/api/index.php');

            const downloadUrl = `${baseUrl}?c=vhost_api&a=file_download&vhost=${jwtData.data.vhost}&file=${encodeURIComponent(file.path)}&token=${encodeURIComponent(jwtData.data.token)}`;

            // 创建临时链接下载
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', file.name);
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            message.error(t('Download failed'));
        }
    };

    const handleCompress = async (file) => {
        try {
            const zipName = `${file.name}.zip`;
            const zipPath = `${currentPath === '/' ? '' : currentPath}/${zipName}`;
            const response = await hostSharedService.compressFiles(
                vhostId,
                [file.path],
                zipPath
            );
            if (response.success) {
                message.success(t('Compressed successfully'));
                loadFiles(currentPath);
            } else {
                message.error(response.error || t('Compression failed'));
            }
        } catch (error) {
            message.error(t('Compression failed'));
        }
    };

    const handleDecompress = (file) => {
        setDecompressFile(file);
        decompressForm.setFieldsValue({
            destination: currentPath === '/' ? '/' : currentPath,
            password: ''
        });
        setDecompressVisible(true);
    };

    const handleDecompressSubmit = async (values) => {
        try {
            const response = await hostSharedService.decompressFile(
                vhostId,
                decompressFile.path,
                values.destination || currentPath,
                values.password || ''
            );
            if (response.success) {
                message.success(t('Extraction successful'));
                setDecompressVisible(false);
                decompressForm.resetFields();
                loadFiles(currentPath);
            } else {
                message.error(response.error || t('Extraction failed'));
            }
        } catch (error) {
            message.error(t('Extraction failed'));
        }
    };

    const handleRemoteValuesChange = (changedValues, allValues) => {
        if (changedValues.url && !allValues.filename) {
            const guess = guessFilenameFromUrl(changedValues.url);
            if (guess) {
                remoteDownloadForm.setFieldsValue({ filename: guess });
            }
        }
    };

    const stopPollingSession = (session) => {
        if (pollingTimers.current[session]) {
            clearTimeout(pollingTimers.current[session]);
            delete pollingTimers.current[session];
        }
    };

    const updateRemoteTask = (session, updater) => {
        setRemoteSessions((prev) =>
            prev.map((task) =>
                task.session === session
                    ? { ...task, ...updater }
                    : task
            )
        );
    };

    const startPollingSession = (session, attempt = 0) => {
        if (!session || !vhostName) {
            return;
        }

        stopPollingSession(session);

        const poll = async (currentAttempt) => {
            try {
                const response = await hostSharedService.queryShell(vhostName, session);
                if (!response.success) {
                    updateRemoteTask(session, {
                        status: 'error',
                        output: response.error || t('Query failed')
                    });
                    stopPollingSession(session);
                    return;
                }
                const { code, output } = response.data || {};
                let status = deriveTaskStatus(code, output, response.data);
                if (status === 'running' && currentAttempt >= MAX_POLL_ATTEMPTS) {
                    status = 'timeout';
                }
                const safeOutput = sanitizeOutput(output);
                updateRemoteTask(session, {
                    status,
                    output: ensureOutputLength(safeOutput),
                    rawOutput: safeOutput,
                    lastCheckedAt: Date.now(),
                    code,
                    responseData: response.data
                });
                if (status !== 'running') {
                    stopPollingSession(session);
                    if (status === 'success') {
                        loadFiles(currentPath);
                    }
                    return;
                }
                pollingTimers.current[session] = setTimeout(
                    () => poll(currentAttempt + 1),
                    POLL_INTERVAL
                );
            } catch (error) {
                updateRemoteTask(session, {
                    status: 'error',
                    output: error.message
                });
                stopPollingSession(session);
            }
        };

        poll(attempt);
    };

    const handleRemoteDownloadSubmit = async (values) => {
        if (!vhostName) {
            message.error(t('Current hostname unknown. Cannot start remote download.'));
            return;
        }
        try {
            const url = values.url?.trim();
            if (!url) {
                message.error(t('Enter a valid download URL'));
                return;
            }
            const dir = normalizeDirInput(values.dir || currentPath);
            const filename = (values.filename || '').trim() || guessFilenameFromUrl(url);
            if (!filename) {
                message.error(t('Please enter a filename to save'));
                return;
            }
            setRemoteDownloadLoading(true);
            const response = await hostSharedService.shellWget(
                vhostName,
                url,
                dir,
                filename,
                values.continue || false
            );
            if (!response.success) {
                message.error(response.error || t('Failed to start remote download'));
                return;
            }
            message.success(response.message || t('Remote download task started'));
            const session = response.data?.session || `local-${Date.now()}`;
            const newTask = {
                session,
                url,
                dir,
                filename,
                status: response.data?.session ? 'running' : 'success',
                output: '',
                startedAt: Date.now()
            };
            setRemoteSessions((prev) => {
                const updated = [newTask, ...prev];
                return updated.slice(0, 8);
            });
            setRemoteDownloadVisible(false);
            remoteDownloadForm.resetFields();
            if (response.data?.session) {
                startPollingSession(session);
            } else {
                loadFiles(currentPath);
            }
        } catch (error) {
            if (error?.errorFields) {
                return;
            }
            message.error(error.message || t('Failed to start remote download'));
        } finally {
            setRemoteDownloadLoading(false);
        }
    };

    const handleManualRefreshSession = (session) => {
        startPollingSession(session);
    };

    const handleRemoveSession = (session) => {
        stopPollingSession(session);
        setRemoteSessions((prev) => prev.filter((task) => task.session !== session));
    };

    const handleRemoteDownloadCancel = () => {
        setRemoteDownloadVisible(false);
        remoteDownloadForm.resetFields();
    };

    const handleOpenRemoteDownload = () => {
        if (!vhostName) {
            message.warning(t('Unable to identify the shared hosting name at the moment. Cannot start the remote download.'));
            return;
        }
        remoteDownloadForm.resetFields();
        remoteDownloadForm.setFieldsValue({
            dir: normalizeDirInput(currentPath),
            continue: false
        });
        setRemoteDownloadVisible(true);
    };

    const columns = [
        {
            title: t('Name'),
            dataIndex: 'name',
            key: 'name',
            render: (name, record) => (
                <Space>
                    {record.type === 'directory' ? <FolderOutlined /> : <FileOutlined />}
                    {record.type === 'directory' ? (
                        <a onClick={() => handleNavigate(record.path)}>{name}</a>
                    ) : (
                        <span>{name}</span>
                    )}
                </Space>
            )
        },
        {
            title: t('Size'),
            dataIndex: 'size',
            key: 'size',
            render: (size, record) => (
                record.type === 'file' ? `${Math.round(size / 1024)}KB` : '-'
            )
        },
        {
            title: t('Permissions'),
            dataIndex: 'permissions',
            key: 'permissions'
        },
        {
            title: t('Access control'),
            key: 'access',
            render: (_, record) => {
                const action = fileAccessMap[record.path];
                return (
                    <Tag color={getAccessTagColor(action)}>
                        {formatAccessLabel(action)}
                    </Tag>
                );
            }
        },
        {
            title: t('Modified at'),
            dataIndex: 'modified',
            key: 'modified'
        },
        {
            title: t('Actions'),
            key: 'action',
            width: 300,
            render: (_, record) => {
                const menuItems = [
                    {
                        key: 'rename',
                        label: t('Rename'),
                        icon: <EditOutlined />,
                        onClick: () => {
                            setRenamingFile(record);
                            renameForm.setFieldsValue({ newname: record.name });
                            setRenameVisible(true);
                        }
                    },
                    {
                        key: 'chmod',
                        label: t('Change permissions'),
                        icon: <LockOutlined />,
                        onClick: () => {
                            setChmodFile(record);
                            setChmodVisible(true);
                        }
                    }
                ];

                // Add file-specific operations
                if (record.type === 'file') {
                    menuItems.unshift(
                        {
                            key: 'edit',
                            label: t('Edit'),
                            icon: <EditOutlined />,
                            onClick: () => handleEditFile(record)
                        },
                        {
                            key: 'download',
                            label: t('Download'),
                            icon: <DownloadOutlined />,
                            onClick: () => handleDownload(record)
                        }
                    );

                    // Add compress for files
                    menuItems.push({
                        key: 'compress',
                        label: t('Compress'),
                        icon: <CompressOutlined />,
                        onClick: () => handleCompress(record)
                    });

                    // Add decompress for archive files
                    if (record.name.match(/\.(zip|tar|gz|tgz|bz2|rar|7z)$/i)) {
                        menuItems.push({
                            key: 'decompress',
                            label: t('Extract'),
                            icon: <FileZipOutlined />,
                            onClick: () => handleDecompress(record)
                        });
                    }
                } else {
                    // Add compress for directories
                    menuItems.push({
                        key: 'compress',
                        label: t('Compress'),
                        icon: <CompressOutlined />,
                        onClick: () => handleCompress(record)
                    });
                }

                return (
                    <Space>
                        {record.type === 'file' && (
                            <Button size="small" icon={<EditOutlined />} onClick={() => handleEditFile(record)}>
                                {t('Edit')}
                            </Button>
                        )}
                        <Button size="small" icon={<LockOutlined />} onClick={() => openAccessModal(record)}>
                            {t('Properties')}
                        </Button>
                        <Dropdown menu={{ items: menuItems }} placement="bottomRight">
                            <Button size="small" icon={<MoreOutlined />}>
                                {t('More')}
                            </Button>
                        </Dropdown>
                        <Popconfirm
                            title={t('Are you sure you want to delete?')}
                            onConfirm={() => handleDeleteFile(record.path)}
                        >
                            <Button size="small" danger icon={<DeleteOutlined />}>
                                {t('Delete')}
                            </Button>
                        </Popconfirm>
                    </Space>
                );
            }
        }
    ];

    return (
        <Card
            title={
                <Breadcrumb>
                    <Breadcrumb.Item>
                        <HomeOutlined onClick={() => handleNavigate('/')} style={{ cursor: 'pointer' }} />
                    </Breadcrumb.Item>
                    {currentPath.split('/').filter(p => p).map((part, index, arr) => (
                        <Breadcrumb.Item key={index}>
                            <a onClick={() => handleNavigate('/' + arr.slice(0, index + 1).join('/'))}>
                                {part}
                            </a>
                        </Breadcrumb.Item>
                    ))}
                </Breadcrumb>
            }
            extra={
                <Space wrap>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={() => loadFiles(currentPath)}
                        loading={loading}
                    >
                        {t('Refresh')}
                    </Button>
                    <Button
                        icon={<CloudDownloadOutlined />}
                        onClick={handleOpenRemoteDownload}
                        disabled={!vhostName}
                    >
                        {t('Remote download')}
                    </Button>
                    {selectedFiles.length > 0 && (
                        <>
                            <Button
                                icon={<CompressOutlined />}
                                onClick={() => {
                                    const zipName = 'archive.zip';
                                    const zipPath = `${currentPath === '/' ? '' : currentPath}/${zipName}`;
                                    hostSharedService.compressFiles(vhostId, selectedFiles, zipPath)
                                        .then(response => {
                                            if (response.success) {
                                                message.success(t('Bulk compression successful'));
                                                setSelectedFiles([]);
                                                loadFiles(currentPath);
                                            } else {
                                                message.error(response.error || t('Bulk compression failed'));
                                            }
                                        });
                                }}
                            >
                                {t("Compress ({{length}})", { length: selectedFiles.length })}
                            </Button>
                            <Popconfirm
                                title={t('Delete the {{length}} selected files?', { length: selectedFiles.length })}
                                onConfirm={() => {
                                    hostSharedService.batchDeleteFiles(vhostId, selectedFiles)
                                        .then(response => {
                                            if (response.success) {
                                                message.success(t('Bulk deletion successful'));
                                                setSelectedFiles([]);
                                                loadFiles(currentPath);
                                            } else {
                                                message.error(response.error || t('Bulk deletion failed'));
                                            }
                                        });
                                }}
                            >
                                <Button danger icon={<DeleteOutlined />}>
                                    {t("Delete ({{length}})", { length: selectedFiles.length })}
                                </Button>
                            </Popconfirm>
                        </>
                    )}
                    <Button icon={<PlusOutlined />} onClick={() => setCreateFolderVisible(true)}>
                        {t('Create folder')}
                    </Button>
                    <Button icon={<PlusOutlined />} onClick={() => setCreateFileVisible(true)}>
                        {t('Create file')}
                    </Button>
                    <Upload
                        multiple
                        showUploadList={false}
                        customRequest={handleUpload}
                    >
                        <Button icon={<UploadOutlined />} loading={uploading > 0}>{t('Upload file')}</Button>
                    </Upload>
                </Space>
            }
        >
            <Table
                dataSource={files}
                columns={columns}
                rowKey="path"
                loading={loading}
                rowSelection={{
                    selectedRowKeys: selectedFiles,
                    onChange: (selectedRowKeys) => {
                        setSelectedFiles(selectedRowKeys);
                    }
                }}
            />


            <Drawer
                title={t('Remote download tasks ({{length}})', { length: remoteSessions.length })}
                open={remoteTaskDrawerVisible}
                onClose={() => setRemoteTaskDrawerVisible(false)}
                width={600}
                bodyStyle={{ paddingBottom: 24 }}
            >
                <Alert
                    message={t('Task instructions')}
                    description={t('Each task runs a wget command on the server. Output is collapsed and can be expanded to view.')}
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
                <List
                    dataSource={remoteSessions}
                    locale={{ emptyText: t('No remote download tasks yet') }}
                    renderItem={(task) => {
                        const meta = REMOTE_STATUS_META[task.status] || REMOTE_STATUS_META.running;
                        const actions = [];
                        if (task.session && task.status === 'running') {
                            actions.push(
                                <Button
                                    key="refresh"
                                    type="link"
                                    size="small"
                                    icon={<ReloadOutlined />}
                                    onClick={() => handleManualRefreshSession(task.session)}
                                >
                                    {t('Refresh')}
                                </Button>
                            );
                        }
                        actions.push(
                            <Button
                                key="remove"
                                type="link"
                                size="small"
                                danger
                                onClick={() => handleRemoveSession(task.session)}
                            >
                                {t('Clear')}
                            </Button>
                        );
                        return (
                            <List.Item key={task.session || (task.filename + '-' + task.startedAt)} actions={actions}>
                                <List.Item.Meta
                                    title={
                                        <Space>
                                            <Tag color={meta.color}>{t(meta.label)}</Tag>
                                            <span>{task.filename}</span>
                                        </Space>
                                    }
                                    description={
                                        <>
                                            <Text type="secondary">
                                                {t("Save path: {{value}}", { value: buildTargetPath(task.dir, task.filename) })}
                                            </Text>
                                            {task.session && (
                                                <div style={{ marginTop: 8 }}>
                                                    <Text type="secondary">Session:</Text>
                                                    <Text
                                                        copyable={{ text: task.session }}
                                                        style={{ marginLeft: 4 }}
                                                    >
                                                        {task.session}
                                                    </Text>
                                                </div>
                                            )}
                                            <div style={{ marginTop: 8 }}>
                                                <Text type="secondary">{t('Latest output:')}</Text>
                                                <Paragraph
                                                    ellipsis={{ rows: 3, expandable: true, symbol: t('View more') }}
                                                    style={{
                                                        background: '#f5f5f5',
                                                        padding: 8,
                                                        borderRadius: 4,
                                                        marginTop: 4,
                                                        fontFamily: 'monospace'
                                                    }}
                                                >
                                                    {task.output || t('Waiting for task output...')}
                                                </Paragraph>
                                            </div>
                                            {task.lastCheckedAt && (
                                                <div>
                                                    <Text type="secondary">
                                                        {t("Last updated: {{value}}", { value: new Date(task.lastCheckedAt).toLocaleTimeString() })}
                                                    </Text>
                                                </div>
                                            )}
                                            {task.responseData?.message && (
                                                <div style={{ marginTop: 4 }}>
                                                    <Text type="secondary">{t('Server details:')}</Text>
                                                    <Text copyable={{ text: task.responseData.message }}>
                                                        {task.responseData.message}
                                                    </Text>
                                                </div>
                                            )}
                                        </>
                                    }
                                />
                            </List.Item>
                        );
                    }}
                />
            </Drawer>
            {/* 新建文件夹 */}
            <Modal
                title={t('Create folder')}
                open={createFolderVisible}
                onCancel={() => {
                    setCreateFolderVisible(false);
                    folderForm.resetFields();
                }}
                onOk={() => folderForm.submit()}
            >
                <Form form={folderForm} onFinish={handleCreateFolder} layout="vertical">
                    <Form.Item name="name" label={t('Folder name')} rules={[{ required: true }]}>
                        <Input placeholder={t('E.g. upload')} />
                    </Form.Item>
                </Form>
            </Modal>

            {/* 新建文件 */}
            <Modal
                title={t('Create file')}
                open={createFileVisible}
                onCancel={() => {
                    setCreateFileVisible(false);
                    fileForm.resetFields();
                }}
                onOk={() => fileForm.submit()}
            >
                <Form form={fileForm} onFinish={handleCreateFile} layout="vertical">
                    <Form.Item name="name" label={t('File name')} rules={[{ required: true }]}>
                        <Input placeholder={t('E.g. index.html')} />
                    </Form.Item>
                    <Form.Item name="content" label={t('File content')}>
                        <TextArea rows={8} />
                    </Form.Item>
                </Form>
            </Modal>

            {/* 编辑文件 */}
            <Modal
                title={t('Edit file: {{name}} ({{status}})', { name: editingFile?.name, status: editingFile?.size ? `${(editingFile.size / 1024).toFixed(2)} KB` : '' })}
                open={editFileVisible}
                onCancel={() => setEditFileVisible(false)}
                onOk={handleSaveFile}
                width={1200}
                style={{ top: 20 }}
                bodyStyle={{ padding: '12px' }}
            >
                <Editor
                    height="600px"
                    language={getLanguageFromFilename(editingFile?.name)}
                    value={fileContent}
                    onChange={(value) => setFileContent(value || '')}
                    theme="vs-dark"
                    options={{
                        fontSize: 14,
                        minimap: { enabled: true },
                        scrollBeyondLastLine: false,
                        automaticLayout: true,
                        tabSize: 4,
                        wordWrap: 'on'
                    }}
                />
            </Modal>

            {/* 重命名 */}
            <Modal
                title={t('Rename')}
                open={renameVisible}
                onCancel={() => {
                    setRenameVisible(false);
                    renameForm.resetFields();
                }}
                onOk={() => renameForm.submit()}
            >
                <Form form={renameForm} onFinish={handleRename} layout="vertical">
                    <Form.Item name="newname" label={t('New name')} rules={[{ required: true, message: t('Please enter a new name') }]}>
                        <Input placeholder={t('Enter a new name')} />
                    </Form.Item>
                </Form>
            </Modal>

            {/* 修改权限 */}
            <Modal
                title={t('Change permissions')}
                open={chmodVisible}
                onCancel={() => {
                    setChmodVisible(false);
                    chmodForm.resetFields();
                }}
                onOk={() => chmodForm.submit()}
            >
                <Form form={chmodForm} onFinish={handleChmod} layout="vertical">
                    <Form.Item
                        name="mode"
                        label={t('Permission mode')}
                        rules={[{ required: true, message: t('Select a permission mode') }]}
                        initialValue="writable"
                    >
                        <Select>
                            <Select.Option value="readonly">{t('Read-only')}</Select.Option>
                            <Select.Option value="writable">{t('Writable')}</Select.Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>

            {/* 解压文件 */}
            <Modal
                title={t('Extract file: {{name}}', { name: decompressFile?.name })}
                open={decompressVisible}
                onCancel={() => {
                    setDecompressVisible(false);
                    decompressForm.resetFields();
                }}
                onOk={() => decompressForm.submit()}
            >
                <Form form={decompressForm} onFinish={handleDecompressSubmit} layout="vertical">
                    <Form.Item
                        name="destination"
                        label={t('Target directory')}
                        rules={[{ required: true, message: t('Enter the destination directory') }]}
                    >
                        <Input placeholder={t('E.g. / or /uploads')} />
                    </Form.Item>
                    <Form.Item name="password" label={t('Password (if any)')}>
                        <Input.Password placeholder={t('Enter the archive password, if any')} />
                    </Form.Item>
                </Form>
            </Modal>

            {/* 远程下载 */}
            <Modal
                title={t('Download remotely to the current directory')}
                open={remoteDownloadVisible}
                onCancel={handleRemoteDownloadCancel}
                onOk={() => remoteDownloadForm.submit()}
                confirmLoading={remoteDownloadLoading}
                destroyOnClose
            >
                <Alert
                    type="info"
                    showIcon
                    message={t('The server downloads remote files directly to your storage. You can track task progress below.')}
                    style={{ marginBottom: 16 }}
                />
                <Form
                    form={remoteDownloadForm}
                    layout="vertical"
                    onValuesChange={handleRemoteValuesChange}
                    onFinish={handleRemoteDownloadSubmit}
                >
                    <Form.Item
                        name="url"
                        label={t('Download address')}
                        rules={[{ required: true, message: t('Please enter a download URL') }]}
                    >
                        <Input placeholder="https://example.com/archive.zip" />
                    </Form.Item>
                    <Form.Item
                        name="dir"
                        label={t('Save directory')}
                        rules={[{ required: true, message: t('Please enter a save directory') }]}
                    >
                        <Input placeholder="/wwwroot" />
                    </Form.Item>
                    <Form.Item
                        name="filename"
                        label={t('Save filename')}
                        rules={[{ required: true, message: t('Please enter a filename') }]}
                    >
                        <Input placeholder="archive.zip" />
                    </Form.Item>
                    <Form.Item name="continue" label={t('Resumable transfers')} valuePropName="checked">
                        <Switch />
                    </Form.Item>
                    <Form.Item shouldUpdate>
                        {() => {
                            const { dir, filename } = remoteDownloadForm.getFieldsValue();
                            const fullPath = buildTargetPath(dir || currentPath, filename);
                            return (
                                <Alert
                                    type="info"
                                    showIcon
                                    message={t('Will be saved to')}
                                    description={<span style={{ wordBreak: 'break-all' }}>{fullPath}</span>}
                                />
                            );
                        }}
                    </Form.Item>
                </Form>
            </Modal>

            <Drawer
                title={t('Remote download tasks ({{length}})', { length: remoteSessions.length })}
                open={remoteTaskDrawerVisible}
                onClose={() => setRemoteTaskDrawerVisible(false)}
                width={600}
                bodyStyle={{ paddingBottom: 24 }}
            >
                <Alert
                    message={t('Task instructions')}
                    description={t('Each task runs a wget command on the server. You can manually refresh the session status or copy the Session for follow-up. Output is split into sections that you can expand to view.')}
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
                <List
                    dataSource={remoteSessions}
                    locale={{ emptyText: t('No remote download tasks yet') }}
                    renderItem={(task) => {
                        const meta = REMOTE_STATUS_META[task.status] || REMOTE_STATUS_META.running;
                        const actions = [];
                        if (task.session && task.status === 'running') {
                            actions.push(
                                <Button
                                    key="refresh"
                                    type="link"
                                    size="small"
                                    icon={<ReloadOutlined />}
                                    onClick={() => handleManualRefreshSession(task.session)}
                                >
                                    {t('Refresh')}
                                </Button>
                            );
                        }
                        actions.push(
                            <Button
                                key="remove"
                                type="link"
                                size="small"
                                danger
                                onClick={() => handleRemoveSession(task.session)}
                            >
                                {t('Clear')}
                            </Button>
                        );
                        return (
                            <List.Item key={task.session || `${task.filename}-${task.startedAt}`} actions={actions}>
                                <List.Item.Meta
                                    title={
                                        <Space>
                                            <Tag color={meta.color}>{t(meta.label)}</Tag>
                                            <span>{task.filename}</span>
                                        </Space>
                                    }
                                    description={
                                        <>
                                            <Text type="secondary">
                                                {t("Save path: {{value}}", { value: buildTargetPath(task.dir, task.filename) })}
                                            </Text>
                                            {task.session && (
                                                <div style={{ marginTop: 8 }}>
                                                    <Text type="secondary">Session:</Text>
                                                    <Text
                                                        copyable={{ text: task.session }}
                                                        style={{ marginLeft: 4 }}
                                                    >
                                                        {task.session}
                                                    </Text>
                                                </div>
                                            )}
                                            <div style={{ marginTop: 8 }}>
                                                <Text type="secondary">{t('Latest output:')}</Text>
                                                <Paragraph
                                                    ellipsis={{ rows: 3, expandable: true, symbol: t('View more') }}
                                                    style={{
                                                        background: '#f5f5f5',
                                                        padding: 8,
                                                        borderRadius: 4,
                                                        marginTop: 4,
                                                        fontFamily: 'monospace'
                                                    }}
                                                >
                                                    {task.output || t('Waiting for task output...')}
                                                </Paragraph>
                                            </div>
                                            {task.lastCheckedAt && (
                                                <div>
                                                    <Text type="secondary">
                                                        {t("Last updated: {{value}}", { value: new Date(task.lastCheckedAt).toLocaleTimeString() })}
                                                    </Text>
                                                </div>
                                            )}
                                            {task.responseData?.message && (
                                                <div style={{ marginTop: 4 }}>
                                                    <Text type="secondary">{t('Server details:')}</Text>
                                                    <Text copyable={{ text: task.responseData.message }}>
                                                        {task.responseData.message}
                                                    </Text>
                                                </div>
                                            )}
                                        </>
                                    }
                                />
                            </List.Item>
                        );
                    }}
                />
            </Drawer>

            <Modal
                title={t('File access control')}
                open={accessModalVisible}
                onCancel={closeAccessModal}
                onOk={handleSubmitAccess}
                confirmLoading={accessSubmitting}
                destroyOnClose
            >
                <Form form={accessForm} layout="vertical">
                    <Form.Item
                        name="act"
                        label={t('Actions')}
                        rules={[{ required: true, message: t('Select an action') }]}
                        initialValue="clear"
                    >
                        <Radio.Group>
                            <Space direction="vertical">
                                <Radio value="clear">{t('Clear')}</Radio>
                                <Radio value="deny">{t('Lock')}</Radio>
                                <Radio value="static">{t('Static')}</Radio>
                                <Radio value="auth">{t('HTTP authentication (allowed users/regex)')}</Radio>
                                <Radio value="ip">{t('Allowed IP ranges (CIDR, comma-separated)')}</Radio>
                            </Space>
                        </Radio.Group>
                    </Form.Item>
                    <Form.Item noStyle shouldUpdate={(prev, cur) => prev.act !== cur.act}>
                        {() => {
                            const current = accessForm.getFieldValue('act');
                            if (current === 'auth') {
                                return (
                                    <Form.Item
                                        label={t('Allowed usernames or regex')}
                                        name="auth_user"
                                        rules={[{ required: true, message: t('Please enter a username or regex') }]}
                                    >
                                        <Input placeholder={t('e.g. admin or ^admin-.*')} />
                                    </Form.Item>
                                );
                            }
                            if (current === 'ip') {
                                return (
                                    <Form.Item
                                        label={t('Allowed IP ranges')}
                                        name="ip"
                                        rules={[{ required: true, message: t('Please enter an IP range') }]}
                                    >
                                        <Input placeholder={t('E.g. 192.168.0.0/16,10.0.0.0/8')} />
                                    </Form.Item>
                                );
                            }
                            return null;
                        }}
                    </Form.Item>
                    <Alert
                        type="info"
                        showIcon
                        message={t('Description')}
                        description={t('Settings apply to the directory immediately: lock/static settings directly modify the access chain; HTTP authentication accepts usernames or regex; IP rules support CIDR, with multiple entries separated by commas.')}
                        style={{ marginTop: 16 }}
                    />
                </Form>
            </Modal>
        </Card>
    );
};

export default FileManager;
